// Project context (docs/plans/2026-10-02-borden-ontwerp.md §12.3): the shell is
// project-focused, so every page under /p/:key knows its project. The key is
// read from the URL; outside /p/... the last project of this user (localStorage
// `goblic:lastProject:<userId>:<workspaceId>`) stays "current", so the sidebar keeps its
// Project group on workspace pages.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { useRouterState } from '@tanstack/react-router'
import type { Project } from '@goblic/shared'
import { projectsApi } from './api-client'
import { useAuth } from './auth'
import { ErrorNote, LoadingRows } from '../components/ui'

export interface CurrentProjectValue {
  /** The project of the URL (/p/:key), else the last used one; null when none exists. */
  project: Project | null
  projects: Project[]
  isLoading: boolean
  /** Remembers `key` as the user's last project (the URL decides while on /p/...). */
  setCurrentProject: (key: string) => void
  /** Most recently used project keys, newest first (for the switcher). */
  recentKeys: string[]
}

const MAX_RECENT = 5

// Per user and workspace: project keys of one workspace mean nothing in another.
const lastKeyStorage = (userId: string, workspaceId: string): string =>
  `goblic:lastProject:${userId}:${workspaceId}`
const recentStorage = (userId: string, workspaceId: string): string =>
  `goblic:recentProjects:${userId}:${workspaceId}`

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Storage unavailable (private mode): the choice just isn't remembered.
  }
}

function readRecent(userId: string, workspaceId: string): string[] {
  try {
    const parsed: unknown = JSON.parse(readStorage(recentStorage(userId, workspaceId)) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : []
  } catch {
    return []
  }
}

/** The project key of a /p/:key/... pathname, or null. */
export function projectKeyFromPath(pathname: string): string | null {
  const match = /^\/p\/([^/]+)/.exec(pathname)
  return match ? decodeURIComponent(match[1]) : null
}

const ProjectContext = createContext<CurrentProjectValue>({
  project: null,
  projects: [],
  isLoading: false,
  setCurrentProject: () => {},
  recentKeys: [],
})

export function ProjectProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const userId = user?.id ?? ''
  const workspaceId = user?.workspaceId ?? ''
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const urlKey = projectKeyFromPath(pathname)

  const [storedKey, setStoredKey] = useState<string | null>(() =>
    userId && workspaceId ? readStorage(lastKeyStorage(userId, workspaceId)) : null
  )
  const [recentKeys, setRecentKeys] = useState<string[]>(() => (userId && workspaceId ? readRecent(userId, workspaceId) : []))

  const projectsQuery = useQuery({
    queryKey: ['projects', workspaceId, 'all'],
    queryFn: () => projectsApi.listAll({ workspaceId }),
    enabled: workspaceId !== '',
    staleTime: 30_000,
  })
  const projects = useMemo(() => projectsQuery.data ?? [], [projectsQuery.data])

  const byKey = useCallback(
    (key: string | null): Project | null =>
      key ? (projects.find((p) => p.key.toUpperCase() === key.toUpperCase()) ?? null) : null,
    [projects]
  )

  // On /p/<unknown> there is no project (the page says so); elsewhere fall back
  // to the stored one, then to the first project.
  const project = urlKey ? byKey(urlKey) : (byKey(storedKey) ?? projects[0] ?? null)

  const remember = useCallback(
    (key: string) => {
      if (!userId || !workspaceId) return
      writeStorage(lastKeyStorage(userId, workspaceId), key)
      setStoredKey(key)
      setRecentKeys((current) => {
        const next = [key, ...current.filter((k) => k !== key)].slice(0, MAX_RECENT)
        writeStorage(recentStorage(userId, workspaceId), JSON.stringify(next))
        return next
      })
    },
    [userId, workspaceId]
  )

  // Visiting a project makes it the last one.
  const urlProjectKey = urlKey ? (byKey(urlKey)?.key ?? null) : null
  useEffect(() => {
    if (urlProjectKey && urlProjectKey !== storedKey) remember(urlProjectKey)
  }, [urlProjectKey, storedKey, remember])

  const value = useMemo<CurrentProjectValue>(
    () => ({
      project,
      projects,
      isLoading: projectsQuery.isLoading,
      setCurrentProject: remember,
      recentKeys,
    }),
    [project, projects, projectsQuery.isLoading, remember, recentKeys]
  )

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
}

export function useCurrentProject(): CurrentProjectValue {
  return useContext(ProjectContext)
}

/** Renders `children` with the project of the URL; handles loading and unknown keys. */
export function RequireProject({ children }: { children: (project: Project) => ReactNode }) {
  const { project, isLoading } = useCurrentProject()
  const urlKey = projectKeyFromPath(
    useRouterState({ select: (state) => state.location.pathname })
  )
  if (isLoading) return <LoadingRows />
  if (!project || (urlKey && project.key.toUpperCase() !== urlKey.toUpperCase())) {
    return (
      <div className="panel">
        <ErrorNote error={new Error(`No project with key ${urlKey ?? ''} in this workspace.`)} what="the project" />
      </div>
    )
  }
  return <>{children(project)}</>
}
